/**
 * PDF Password Modal
 * 
 * Prompts the user for a password when a password-protected PDF is detected.
 * Delegates password validation to the onSubmit handler, which may validate
 * client-side (pdf.js) or server-side (backend API) depending on context.
 * 
 * The onSubmit handler should throw an Error with a user-friendly message
 * if the password is invalid.
 */

import React, { useState, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SectionTitle from "../common/SectionTitle";

interface PdfPasswordModalProps {
  open: boolean;
  file: File | null;
  onSubmit: (password: string) => void | Promise<void>;
  onSkip: () => void;
  onCancel: () => void;
}

const PdfPasswordModal: React.FC<PdfPasswordModalProps> = ({
  open,
  file,
  onSubmit,
  onSkip,
  onCancel,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError(null);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = useCallback(async () => {
    if (!file || !password.trim()) {
      setError("Please enter a password");
      return;
    }

    setValidating(true);
    setError(null);

    try {
      // Delegate validation to the onSubmit handler.
      // The handler validates (client-side or backend) and throws if invalid.
      await onSubmit(password);
      // If onSubmit succeeded without throwing, the password was accepted
      setPassword("");
      setError(null);
    } catch (err: any) {
      setError(err.message || "Incorrect password. Please try again.");
    } finally {
      setValidating(false);
    }
  }, [file, password, onSubmit]);

  const handleSkip = () => {
    setPassword("");
    setError(null);
    onSkip();
  };

  const handleCancel = () => {
    setPassword("");
    setError(null);
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && password.trim() && !validating) {
      handleSubmit();
    }
  };

  return (
    <Modal open={open} onClose={handleCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 450 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <SectionTitle title="Password-Protected PDF" />
        
        {/* File info with lock icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 1,
          }}
        >
          <Box sx={{ position: "relative" }}>
            <InsertDriveFileIcon sx={{ fontSize: 40, color: "error.main" }} />
            <LockIcon
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                fontSize: 18,
                color: "warning.main",
                bgcolor: "background.paper",
                borderRadius: "50%",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file?.name || "Unknown file"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This PDF is password-protected
            </Typography>
          </Box>
        </Box>

        {/* Password input */}
        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          label="Enter PDF password"
          value={password}
          onChange={handlePasswordChange}
          onKeyDown={handleKeyDown}
          disabled={validating}
          error={!!error}
          autoFocus
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={handleCancel}
            disabled={validating}
          >
            Cancel All
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleSkip}
            disabled={validating}
          >
            Skip This File
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={validating || !password.trim()}
            startIcon={
              validating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LockIcon />
              )
            }
          >
            {validating ? "Validating..." : "Unlock"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PdfPasswordModal;
